<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Button from '../../Button.svelte';
  import EmployeeDonationService from '../../../lib/api/employee/donation.service';

  export let open: boolean = false;
  export let donationUuid: string = '';
  export let charityName: string = '';
  export let leaveHours: number = 0;

  const dispatch = createEventDispatcher();

  let loading: boolean = false;
  let error: string = '';

  $: if (open) {
    error = '';
  }

  async function handleConfirm() {
    loading = true;
    error = '';

    try {
      const result = await EmployeeDonationService.cancelDonationRequest(donationUuid);

      if (result.success) {
        dispatch('success', { message: 'Donation request cancelled successfully' });
        handleClose();
      } else {
        error = result.message || 'Failed to cancel donation request';
      }
    } catch (err: any) {
      error = err.message || 'An unexpected error occurred';
    } finally {
      loading = false;
    }
  }

  function handleClose() {
    if (!loading) {
      error = '';
      dispatch('close');
    }
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if open}
  <div 
    class="modal-backdrop" 
    role="presentation"
    on:click|self={handleClose}
  >
    <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="cancel-donation-title">
      <div class="modal-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>

      <h2 id="cancel-donation-title" class="modal-title">Cancel Donation Request?</h2>
      
      <p class="modal-desc">
        Are you sure you want to cancel this donation request? This action cannot be undone.
      </p>

      <div class="donation-summary">
        <div class="summary-row">
          <span class="summary-label">Charity:</span>
          <span class="summary-value">{charityName}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Hours:</span>
          <span class="summary-value">{leaveHours} hours</span>
        </div>
      </div>

      {#if error}
        <div class="error-message" role="alert">{error}</div>
      {/if}

      <div class="modal-actions">
        <Button 
          variant="primary" 
          fullWidth={true}
          on:click={handleConfirm}
          disabled={loading}
        >
          {loading ? "Cancelling..." : "Yes, Cancel Request"}
        </Button>
        <Button 
          variant="secondary" 
          fullWidth={true}
          on:click={handleClose}
          disabled={loading}
        >
          No, Keep Request
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(31, 41, 55, 0.5);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .modal-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    width: 100%;
    max-width: 420px;
    padding: 2rem;
    text-align: center;
  }

  .modal-icon {
    width: 64px;
    height: 64px;
    background-color: #fef3c7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: #f59e0b;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.75rem;
  }

  .modal-desc {
    font-size: 1rem;
    color: #6b7280;
    margin: 0 0 1.5rem;
    line-height: 1.5;
  }

  .donation-summary {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0;
  }

  .summary-row:first-child {
    padding-top: 0;
  }

  .summary-row:last-child {
    padding-bottom: 0;
  }

  .summary-label {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .summary-value {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .error-message {
    font-size: 0.875rem;
    color: #ef4444;
    background-color: #fef2f2;
    border: 1px solid #fee2e2;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1.5rem;
    text-align: left;
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (max-width: 768px) {
    .modal-card {
      padding: 1.5rem;
    }
  }
</style>
